import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { AUTH_PHONE_SCHEMA } from "../../../helpers/validation-schema";
import { useGenerateOtpMutation } from "../../../store/api/authApi";
import { REGEX } from "../../../constants/regex";
import { addPhoneNumberPrefix } from "../../../helpers";
import { toastSuccess } from "../../../components/toast";
import Button from "../../../components/common/button";
import Input from "../../../components/common/input/input";
import AuthLayout from "../../../components/layouts/auth";
import { ROUTES } from "../../../constants/routes";
import { SelectMenuOption, COUNTRIES } from "../../../helpers/index";
import React, { useState } from "react";
import { CountrySelector } from "../../../components/common/select/country-select";
import LottieAnimation from "../../../components/common/animations";

const AuthPhoneNUmber = () => {
  const [selectedCountry, setSelectedCountry] = useState<SelectMenuOption>(
    COUNTRIES[104],
  );
  const [countrySelectorOpen, setCountrySelectorOpen] = useState(false);
  const navigate = useNavigate();
  const [generateOtp] = useGenerateOtpMutation();

  type MobileFormData = {
    mobile: string;
    countryCode: string;
  };

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<MobileFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(AUTH_PHONE_SCHEMA),
    defaultValues: {
      mobile: "",
      countryCode: COUNTRIES[104].code,
    },
  });

  const validatePhoneRgex = (value: string) =>
    REGEX.NUMBER_KEY_PRESS.test(value);

  const onSubmit = async (data: MobileFormData) => {
    const phoneNo = addPhoneNumberPrefix(data?.mobile, data?.countryCode);
    const response: any = await generateOtp({ phone: phoneNo });
    if (response?.data?.success) {
      toastSuccess({ message: response?.data?.message });
      navigate(`${ROUTES.AUTH_PHONE_OTP}?phone=${phoneNo}`);
    }
  };

  return (
    <AuthLayout>
      <form className="flex w-full h-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="h-full flex flex-col justify-between">
          <div>
            <div className="w-32 md:mb-10 mb-6">
              <LottieAnimation
                lottieUrl="/animation/Logo.lottie"
                height="70px"
              />
            </div>

            <div className="text-3xl text-white leading-10 font-bold mb-6">
              Enter your mobile number to get OTP
            </div>
            <div className="flex items-start w-full">
              <Controller
                name="countryCode"
                control={control}
                render={({ field: { onChange } }) => (
                  <CountrySelector
                    id="country-selector"
                    open={countrySelectorOpen}
                    onToggle={() =>
                      setCountrySelectorOpen(!countrySelectorOpen)
                    }
                    onChange={(selectedValue: {
                      value: string;
                      code: string;
                    }) => {
                      onChange(selectedValue.code);
                      setSelectedCountry(
                        COUNTRIES.find(
                          (c) => c.value === selectedValue.value,
                        ) || COUNTRIES[0],
                      );
                      setCountrySelectorOpen(false);
                    }}
                    selectedValue={
                      COUNTRIES.find(
                        (c) => c.value === selectedCountry.value,
                      ) || COUNTRIES[0]
                    }
                  />
                )}
              />
              <div className="ml-2 w-full">
                <Controller
                  name="mobile"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <Input
                      id="mobile"
                      name="mobile"
                      customClassName="bg-[#262626] text-white px-3"
                      validate={validatePhoneRgex}
                      showError={true}
                      errorMessage={errors?.mobile?.message}
                      value={value}
                      onChange={onChange}
                      placeholder="1234567890"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div>
            <Button
              isSubmitting={isSubmitting}
              customClasses="w-full mb-5 rounded-xl"
            >
              Next
            </Button>
            <div className="text-gray-400 text-xs justify-center items-center flex flex-wrap text-center gap-1">
              By clicking, I accept the
              <a
                href="https://www.onepercentclub.io/terms-conditions"
                className="text-white"
              >
                Terms & Conditions
              </a>
              &
              <a
                href="https://www.onepercentclub.io/privacy-policy"
                className="text-white"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AuthPhoneNUmber;
